// Central logger module. Wraps console + leitet Fehler an Sentry weiter (P2).
//
// PII-Regel: NIEMALS Klarnamen, E-Mail-Adressen oder Reflexions-/Freitexte von
// Kindern in Log-Messages interpolieren (Vercel-/Service-Retention).
// Meldungen sind statischer Diagnose-Kontext. Zusätzliche Argumente werden auf
// die unten erlaubten technischen Felder begrenzt, nie als Rohtext ausgegeben.
//
// Sentry: capture* sind No-ops, solange Sentry nicht initialisiert ist (kein DSN) —
// in Dev/ohne Account also wirkungslos. beforeSend (src/lib/sentry.ts) entfernt
// Exception-Texte und zusätzliche Rohfelder; die Stackframes bleiben erhalten.
//
// Der Sentry-Zugriff ist ein dynamischer Import (fire-and-forget): logger wird
// auch von 'use client'-Error-Boundaries importiert — ein statischer
// `import * as Sentry` züge das SDK in den First-Load jeder Route
// (Perf-Audit 2026-07-16, R3). logger.error bleibt synchron (void).

const ERROR_TYPES = new Set([
  'Error', 'TypeError', 'SyntaxError', 'RangeError', 'ReferenceError', 'URIError',
  'EvalError', 'AggregateError', 'AbortError', 'TimeoutError', 'NetworkError',
  'NotFoundError', 'InvalidStateError', 'SecurityError', 'ZodError', 'APIError',
  'UnrecognizedActionError', 'ChunkLoadError', 'DiagnosticReadError',
]);
const NUMBER_FIELDS = [
  'status', 'timeoutMs', 'promptTokens', 'completionTokens', 'totalTokens',
  'reasoningTokens', 'latencyMs', 'diskBusyRatio', 'cpuLoadRatio', 'memAvailablePct',
] as const;
const NULLABLE_FIELDS = new Set(['latencyMs', 'diskBusyRatio', 'cpuLoadRatio', 'memAvailablePct']);
const ENUM_FIELDS: Record<string, ReadonlySet<string>> = {
  model: new Set(['gpt-5.6-sol', 'gpt-5.4', 'gpt-5.2-pro', 'gpt-5.2', 'claude-opus-4-8']),
  finishReason: new Set(['stop', 'length', 'content_filter', 'tool_calls', 'function_call', 'unknown']),
  errorType: ERROR_TYPES,
  code: new Set(['23505', '23503', '23514']),
};
const LIST_FIELDS: Record<string, ReadonlySet<string>> = {
  breaches: new Set(['db-probe-failed', 'latency', 'disk-busy', 'cpu-load']),
  notes: new Set([
    'mem-knapp', 'metrik-fehlt:cpu-load', 'metrik-fehlt:mem-available',
    'metrics-auth-abgelehnt', 'metrics-endpunkt-nicht-erreichbar', 'metrics-uebersprungen',
  ]),
};

type LogMetadata = Record<string, string | number | boolean | null | string[]>;

export function safeErrorType(value: unknown): string {
  return typeof value === 'string' && ERROR_TYPES.has(value) ? value : 'Error';
}

/** Technische Felder für Console und Sentry; unbekannte Nutzlasten entfallen. */
export function sanitizeLogArgs(args: unknown[]): LogMetadata[] {
  return args.flatMap((arg) => {
    if (!arg || typeof arg !== 'object' || Array.isArray(arg)) return [];
    try {
      const fields = Object.getOwnPropertyDescriptors(arg);
      const data: LogMetadata = {};
      if (arg instanceof Error) data.errorType = safeErrorType(arg.name);
      for (const key of NUMBER_FIELDS) {
        const value = fields[key]?.value;
        if ((typeof value === 'number' && Number.isFinite(value)) || (value === null && NULLABLE_FIELDS.has(key))) {
          data[key] = value;
        }
      }
      for (const [key, allowed] of Object.entries(ENUM_FIELDS)) {
        const value = fields[key]?.value;
        if (typeof value === 'string' && allowed.has(value)) data[key] = value;
      }
      if (typeof fields.refused?.value === 'boolean') data.refused = fields.refused.value;
      for (const [key, allowed] of Object.entries(LIST_FIELDS)) {
        const value = fields[key]?.value;
        if (Array.isArray(value)) {
          data[key] = value.filter((item): item is string => typeof item === 'string' && allowed.has(item));
        }
      }
      return Object.keys(data).length ? [data] : [];
    } catch {
      // Auch ein fehlerhafter Getter/Proxy darf den ursprünglichen Fehler nicht überdecken.
      return [];
    }
  });
}

export const logger = {
  error: (message: string, ...args: unknown[]) => {
    const safeArgs = sanitizeLogArgs(args);
    console.error(`[ERROR] ${message}`, ...safeArgs);
    const err = args.find((a): a is Error => a instanceof Error);
    const extra = { logMessage: message, ...(safeArgs.length ? { args: safeArgs } : {}) };
    import('@sentry/nextjs')
      .then((Sentry) => {
        if (err) {
          // SDK extrahiert den Original-Stack; beforeSend entfernt alle Fehlertexte.
          Sentry.captureException(err, { extra });
        } else {
          Sentry.captureMessage(message, {
            level: 'error',
            extra,
          });
        }
      })
      .catch(() => {});
  },
  warn: (message: string, ...args: unknown[]) => {
    console.warn(`[WARN] ${message}`, ...sanitizeLogArgs(args));
  },
  info: (message: string, ...args: unknown[]) => {
    if (process.env.NODE_ENV !== 'production') {
      console.info(`[INFO] ${message}`, ...sanitizeLogArgs(args));
    }
  },
};

/**
 * Maskiert eine E-Mail-Adresse für Logs/Diagnose: 'max.muster@schule.ch' -> 'm***@schule.ch'.
 * Nur verwenden, wenn die Domain wirklich gebraucht wird — sonst lieber die UUID loggen.
 */
export function redactEmail(email: string | null | undefined): string {
  if (!email) return '<none>';
  const at = email.indexOf('@');
  if (at <= 0) return '***';
  return `${email[0]}***${email.slice(at)}`;
}
